import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Save } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { updateFraudElement } from "../logic/fraudElementsThunk";
import { selectFraudElementById } from "../logic/fraudElementsSlice";
import { FRAUD_ELEMENT_CATEGORIES_LABELS } from "../../../api/fraudElements";
import { cn } from "../../../lib/utils";
import { showErrorToast } from "../../../lib/toast";
import EditToggleButton from "../../../components/ui/button/v2/EditToggleButton";

interface FraudElementDetailsCardProps {
  fraudElementId: string;
}

export default function FraudElementDetailsCard({
  fraudElementId,
}: FraudElementDetailsCardProps) {
  const dispatch = useAppDispatch();
  const { id: caseId } = useParams<{ id: string }>();

  const fraudElement = useAppSelector((state) =>
    selectFraudElementById(state, fraudElementId)
  );

  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setDraft(fraudElement?.details ?? "");
  }, [fraudElement?.details]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = "0px";
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [draft, isEditing]);

  if (!fraudElement) {
    return null;
  }

  const hasChanges = draft !== (fraudElement.details ?? "");

  const handleCancel = () => {
    setDraft(fraudElement.details ?? "");
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!caseId || !hasChanges || isSaving) return;

    setIsSaving(true);
    try {
      await dispatch(
        updateFraudElement({
          case_id: caseId,
          fraud_element_id: fraudElementId,
          request: { details: draft },
        })
      );
      setIsEditing(false);
    } catch {
      showErrorToast("Failed to update details. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className={cn(
        "overflow-hidden transition-all duration-200 bg-[#F9FAFB] font-[Inter]",
        isEditing
          ? "rounded-[14px] border-[3px] border-[#C0C9FB]"
          : "rounded-[8px]"
      )}
    >
      <div className="p-3">
        <div className="flex items-center justify-between">
          <span className="text-[16px] font-medium leading-[22px] text-[#334155]">
            {FRAUD_ELEMENT_CATEGORIES_LABELS[fraudElement.fraud_element_category]}
          </span>

          <EditToggleButton
            isEditing={isEditing}
            onEdit={() => setIsEditing(true)}
            onCancel={handleCancel}
            className="mr-1"
          />
        
        </div>

        <div className="mt-3">
          {isEditing ? (
            <textarea
              ref={textareaRef}
              value={draft}
              disabled={isSaving}
              placeholder="Enter details..."
              rows={draft ? 1 : 5}
              onChange={(e) => setDraft(e.target.value)}
              className={cn(
                "w-full resize-none overflow-hidden border-none bg-transparent p-0",
                "text-[14px] leading-5 text-[#334155]",
                "placeholder:text-[#737373]",
                "focus:outline-none focus:ring-0",
                !draft && "min-h-[70px]"
              )}
            />
          ) : fraudElement.details ? (
            <div className="whitespace-pre-wrap break-words text-[14px] leading-5 text-[#334155]">
              {fraudElement.details}
            </div>
          ) : (
            <div className="whitespace-pre-wrap break-words text-sm leading-5 text-[#737373]">
              Enter details...
            </div>
          )}
        </div>
      </div>

      {isEditing && hasChanges && (
        <div className="flex h-[52px] items-center justify-between border-t border-[#E5E5E5] bg-[#F8F9FA] px-4 py-3">
          <span className="text-sm font-medium leading-5 text-[#737373]">
            You have made changes, save now
          </span>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCancel}
              disabled={isSaving}
              className="flex items-center justify-center rounded-[8px] bg-transparent px-4 py-2 text-sm font-medium leading-5 text-[#0A0A0A] opacity-50"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center justify-center gap-2 rounded-[8px] bg-gradient-to-b from-[#788AF7] to-[#3551F3] px-4 py-1 text-sm font-medium leading-5 text-[#FEF2F2] shadow-[0_1px_2px_rgba(0,0,0,0.05)] transition-all hover:opacity-90"
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin text-[#FEF2F2]" />
              ) : (
                <Save className="h-4 w-4 text-[#FEF2F2]" />
              )}
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}